/* PR 45 review · D2 (Card V3) — country, currency and weekend fields, shared by the create form (Engagements) and
 * Engagement Settings. No default: the three start empty on create. The country pre-ticks a weekend suggestion while the
 * user has not touched the weekend; "No weekend days" is an explicit choice. Currency: a searchable ISO 4217 list (code +
 * name) plus "Other – type your own" (trimmed, 1–10 characters, no < or >, stored exactly as typed). */
import React, { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  COUNTRY_CODES, CURRENCY_CODES, CURRENCY_MAX_LEN, OTHER, WEEKDAYS, countryName, currencyName, isListedCurrency,
  otherCurrencyError, suggestedWeekend,
} from "@/lib/engagementLocale";
import { suggestCurrencyCode } from "@/lib/currencySuggest";   // Batch 32

/** The form value for a stored engagement (Settings) or an empty one (create). */
export function localeValueOf(eng) {
  const cur = String(eng?.currency ?? "").trim();
  const wd = eng?.non_working_days?.weekdays;
  const saved = Array.isArray(wd);
  return {
    country: String(eng?.country || "").toUpperCase(),
    currencyChoice: cur ? (isListedCurrency(cur) ? cur : OTHER) : "",
    currencyOther: cur && !isListedCurrency(cur) ? cur : "",
    weekend_days: saved ? [...wd].sort() : [],
    no_weekend: saved && wd.length === 0,
    weekendTouched: saved,
  };
}

export const currencyOfValue = (v) => (v.currencyChoice === OTHER ? String(v.currencyOther || "").trim() : (v.currencyChoice || ""));

/** Field errors (i18n keys under engLocale.*), empty object when the three are complete. */
export function localeErrors(v) {
  const e = {};
  if (!v.country) e.country = "countryRequired";
  if (!v.currencyChoice) e.currency = "currencyRequired";
  else if (v.currencyChoice === OTHER) {
    const why = otherCurrencyError(v.currencyOther);
    if (why) e.currency = why === "required" ? "otherRequired" : "otherInvalid";
  }
  if (!(v.weekend_days || []).length && !v.no_weekend) e.weekend = "weekendRequired";
  return e;
}

export const localePayload = (v) => ({
  country: v.country, currency: currencyOfValue(v),
  weekend_days: v.no_weekend ? [] : [...(v.weekend_days || [])].sort(), no_weekend: !!v.no_weekend,
});

// suggestOnCountry: on create the country pre-ticks its usual weekend; in Engagement Settings a country change never changes
// the weekend (the dialog shows a hint when the two differ)
export default function EngagementLocaleFields({ value, onChange, t, lang, errors = {}, disabled = false, testIdPrefix = "eng-locale",
                                                 suggestOnCountry = true }) {
  const [cq, setCq] = useState("");
  const [uq, setUq] = useState("");
  const set = (patch) => onChange({ ...value, ...patch });
  const countries = useMemo(() => COUNTRY_CODES.map((c) => ({ code: c, name: countryName(c, lang) }))
    .sort((a, b) => a.name.localeCompare(b.name, lang === "ar" ? "ar" : "en")), [lang]);
  const currencies = useMemo(() => CURRENCY_CODES.map((c) => ({ code: c, name: currencyName(c, lang) })), [lang]);
  const cf = cq.trim().toLowerCase();
  const uf = uq.trim().toLowerCase();
  const shownCountries = countries.filter((c) => !cf || c.name.toLowerCase().includes(cf) || c.code.toLowerCase() === cf || c.code === value.country);
  const shownCurrencies = currencies.filter((c) => !uf || c.code.toLowerCase().includes(uf) || c.name.toLowerCase().includes(uf) || c.code === value.currencyChoice);
  const pickCountry = (code) => {
    const patch = { country: code };
    if (suggestOnCountry && !value.weekendTouched) {   // the country pre-ticks a suggestion until the user ticks the weekend
      const s = suggestedWeekend(code);
      if (s) { patch.weekend_days = s; patch.no_weekend = false; }
    }
    set(patch);
  };
  const toggleDay = (d) => {
    const has = (value.weekend_days || []).includes(d);
    const days = has ? value.weekend_days.filter((x) => x !== d) : [...(value.weekend_days || []), d].sort();
    set({ weekend_days: days, no_weekend: false, weekendTouched: true });
  };
  const err = (k) => errors[k] ? <p className="mt-1 text-xs text-red-600" data-testid={`${testIdPrefix}-${k}-error`}>{t(`engLocale.${errors[k]}`)}</p> : null;
  const box = (k) => `mt-1 w-full rounded-md border bg-background px-2 py-1.5 text-sm ${errors[k] ? "border-red-500" : "border-input"}`;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid={testIdPrefix}>
      <div>
        <Label>{t("engLocale.country")} <span className="text-red-600">*</span></Label>
        <Input className="mt-1 h-8" value={cq} onChange={(e) => setCq(e.target.value)} placeholder={t("engLocale.countrySearch")}
               disabled={disabled} data-testid={`${testIdPrefix}-country-search`} />
        <select className={box("country")} value={value.country || ""} disabled={disabled} aria-invalid={!!errors.country}
                onChange={(e) => pickCountry(e.target.value)} data-testid={`${testIdPrefix}-country`}>
          <option value="">{t("engLocale.countryPick")}</option>
          {shownCountries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        {err("country")}
      </div>
      <div>
        <Label>{t("engLocale.currency")} <span className="text-red-600">*</span></Label>
        <Input className="mt-1 h-8" value={uq} onChange={(e) => setUq(e.target.value)} placeholder={t("engLocale.currencySearch")}
               disabled={disabled} data-testid={`${testIdPrefix}-currency-search`} />
        <select className={box("currency")} value={value.currencyChoice || ""} disabled={disabled} aria-invalid={!!errors.currency}
                onChange={(e) => set({ currencyChoice: e.target.value })} data-testid={`${testIdPrefix}-currency`}>
          <option value="">{t("engLocale.currencyPick")}</option>
          {shownCurrencies.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
          <option value={OTHER}>{t("engLocale.other")}</option>
        </select>
        {value.currencyChoice === OTHER && (
          <div className="mt-2">
            <Label className="text-xs">{t("engLocale.otherLabel")}</Label>
            <Input className={`mt-1 h-8 ${errors.currency ? "border-red-500" : ""}`} value={value.currencyOther || ""} maxLength={CURRENCY_MAX_LEN + 5}
                   disabled={disabled} onChange={(e) => set({ currencyOther: e.target.value })} data-testid={`${testIdPrefix}-currency-other`} />
          </div>
        )}
        {err("currency")}
        {errors.currency === "otherInvalid" && suggestCurrencyCode(value.currencyOther) ? (   /* Batch 32: the ISO code for a name */
          <p className="mt-1 text-xs text-red-600" data-testid={`${testIdPrefix}-currency-suggest`}>
            {t("b32f.currencyUseIso").replace("{code}", suggestCurrencyCode(value.currencyOther))}
          </p>
        ) : null}
      </div>
      <div className="sm:col-span-2">
        <Label>{t("engLocale.weekend")} <span className="text-red-600">*</span></Label>
        <p className="text-[11px] text-muted-foreground">{t("engLocale.weekendHint")}</p>
        <div className={`mt-1 flex flex-wrap gap-2 rounded-md border p-2 ${errors.weekend ? "border-red-500" : "border-input"}`} role="group"
             aria-label={t("engLocale.weekend")} data-testid={`${testIdPrefix}-weekend`}>
          {WEEKDAYS.map((d) => (
            <label key={d} className="flex items-center gap-1 text-xs">
              <input type="checkbox" checked={!value.no_weekend && (value.weekend_days || []).includes(d)} disabled={disabled}
                     onChange={() => toggleDay(d)} data-testid={`${testIdPrefix}-day-${d}`} />
              {t(`engLocale.days.${d}`)}
            </label>
          ))}
          <label className="ms-auto flex items-center gap-1 text-xs">
            <input type="checkbox" checked={!!value.no_weekend} disabled={disabled}
                   onChange={(e) => set({ no_weekend: e.target.checked, weekend_days: e.target.checked ? [] : value.weekend_days, weekendTouched: true })}
                   data-testid={`${testIdPrefix}-no-weekend`} />
            {t("engLocale.noWeekend")}
          </label>
        </div>
        {err("weekend")}
      </div>
    </div>
  );
}
